import {
  groupControllerCreateGroup,
  groupControllerGetGroups,
  groupUserLinkControllerUnlinkUser,
} from "@/lib/gen-api/sdk.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import { queryKeys } from "../lib/query-keys";

export const useGlobalGroupsData = (userUuid?: string) => {
  const queryClient = useQueryClient();
  const filter = userUuid ? { user_uuid: userUuid } : undefined;

  const {
    data: groups,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady(),
    queryKey: queryKeys.groups(userUuid),
    queryFn: () =>
      groupControllerGetGroups({
        query: {
          filter,
        },
      }).then((res) => res.data?.data ?? []),
  });

  const { mutateAsync: createGroup, isPending: isCreatingGroup } = useMutation({
    mutationFn: async (params: {
      name: string;
      slug: string;
      parentGroupUuid?: string;
    }) => {
      const response = await groupControllerCreateGroup({
        body: params,
      });

      if (response.response.status !== 201) {
        throw new Error(
          `Failed to create group: ${response.response.statusText}`,
        );
      }

      return response.data!;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groups(userUuid),
      });
    },
  });

  const { mutateAsync: unlinkGroup } = useMutation({
    mutationFn: (groupUuid: string) =>
      groupUserLinkControllerUnlinkUser({
        path: { groupUuid, userUuid: userUuid! },
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groups(userUuid),
      });
    },
  });

  return {
    groups: groups ?? [],
    isLoading: isPending,
    error,
    unlinkGroup,
    createGroup,
    isCreatingGroup,
  };
};
